// pages/superadmin/AiPerformanceDashboard.jsx
//
// Phase 5A/5B, AI Performance Dashboard, rendered as the "AI Performance"
// tab inside CustomerDashboard. Mirrors QAQueue / SalesWorkspace styling.
// Renders metrics from aiPerformanceService.js. Scoping (internal vs one
// partner's own figures) happens server-side based on the logged-in user,
// so this component has no role-specific logic.
//
// Meetings Generated and Revenue Won are now backed by RevenueOutcome,
// a manual outcome log a rep/partner fills in from SalesWorkspace.jsx
// (see the "Log Outcome" action there). Renders as "-" only when the
// dashboard payload's trackingAvailable flag is unexpectedly false (e.g.
// an older API version), not as a fake 0.
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import useRevenueApi from "../../../hooks/useRevenueApi";
import InfoTooltip from "../../../components/InfoTooltip";
import useSubnavSlot from "../../../components/useSubnavSlot";
import { createPortal } from "react-dom";
import { scopeCss } from "../../../utils/scopeCss";
import { LineChart } from "../../../arc/components/line-chart/line-chart";
import { DonutChart } from "../../../arc/components/donut-chart/donut-chart";
import { Skeleton } from "../../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../../utils/themeTransition";

const EMBED_SCOPE = ".ri-embed-ai-perf";

// Admin-only analytics (internal team / superadmin) are never shown to partners.
const INTERNAL_DOMAINS = ["peachloves.co.uk", "peachlovesdigital.co.uk"];
const isInternalUser = (email) => {
  const domain = (email || "").split("@")[1]?.toLowerCase();
  return !!domain && INTERNAL_DOMAINS.includes(domain);
};

const styles = `
  /* Cal.com-style system: white canvas, hairline cards, near-black ink,
   * sentence-case Inter labels, status shown as a dot not a tint. Mirrors
   * SalesWorkspace.jsx. */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }



  /* dark: flat cards need a hairline to separate from the #101010 canvas */
  .dash-root.dark .metric-card.is-hero { border-color: var(--border); }

  html, body { overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; }

  /* HEADER, flat AXO navy, same as SalesWorkspace */
  .dash-header { background: var(--header-bg); border-bottom: 1px solid var(--header-border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1200px; margin: 0 auto; padding: 0 28px; min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .dash-brand { display: flex; align-items: center; gap: 12px; padding: 12px 0; }
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: var(--radius-sm); color: var(--header-text); text-decoration: none; }
  .btn-back:hover { background: var(--header-btn-hover-bg); color: #fff; }
  .dash-brand-mark { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; border-radius: var(--radius-sm); background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); color: #fff; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-size: 12px; color: var(--header-text-muted); }
  .dash-header-actions { display: flex; align-items: center; gap: 8px; padding: 12px 0; }
  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: var(--header-text); cursor: pointer; white-space: nowrap; text-decoration: none; transition: background var(--transition), color var(--transition), border-color var(--transition); }
  .btn-header:hover:not(:disabled) { background: var(--header-btn-hover-bg); color: #fff; }
  .btn-header:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-header:focus-visible { outline: 2px solid rgba(255,255,255,0.6); outline-offset: 2px; }
  .btn-header.is-accent { background: rgba(255,255,255,0.12); }
  .btn-header svg { flex-shrink: 0; }
  .btn-header.is-refreshing svg { animation: refresh-spin 0.6s linear; }
  @keyframes refresh-spin { to { transform: rotate(360deg); } }

  .dash-main { max-width: 1200px; margin: 0 auto; padding: 32px 28px 56px; display: flex; flex-direction: column; gap: 28px; min-width: 0; }
  .dash-main > div { animation: card-in 0.25s ease both; }
  @keyframes card-in { from { opacity: 0; } to { opacity: 1; } }

  /* SECTIONS, plain sentence-case heading, no rule, no mono */
  .section-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
  .section-title { display: inline-flex; align-items: center; gap: 4px; font-size: 16px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); }
  .section-caption { font-size: 13px; color: var(--text-muted); }

  /* METRIC CARDS, white + hairline; grey fill marks the headline figure */
  .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
  .metric-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 22px; min-width: 0; }
  .metric-card.is-hero { background: var(--surface-sunken); border-color: transparent; }
  .metric-label { display: flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 500; color: var(--text-muted); margin-bottom: 8px; }
  .metric-value { font-size: 28px; font-weight: 600; line-height: 1.15; color: var(--text); letter-spacing: -0.03em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .metric-value.muted { color: var(--text-faint); font-size: 15px; font-weight: 500; letter-spacing: 0; }
  .metric-note { font-size: 13px; color: var(--text-muted); margin-top: 6px; }

  /* BADGE, neutral pill, a small dot carries the status */
  .badge { display: inline-flex; align-items: center; gap: 6px; font-family: var(--sans); font-size: 12px; font-weight: 500; letter-spacing: 0; text-transform: none; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); border: none; color: var(--text); white-space: nowrap; }
  .badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .badge.confidence-moderate, .badge.direction-growing { --dot: var(--green); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.confidence-low { --dot: var(--amber); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.direction-declining { --dot: var(--red); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.confidence-insufficient_data, .badge.direction-flat { --dot: var(--text-faint); color: var(--text-muted); background: var(--surface-sunken); border: none; }

  /* CHART, one ink colour, no gradient */
  .chart-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; }
  .chart-stack { display: grid; gap: 16px; }
  .chart-empty { color: var(--text-muted); font-size: var(--text-sm); }
  .insights-note { margin: 0 0 12px; font-size: var(--text-sm); color: var(--text-muted); }
  .stage-bottleneck { display: inline-flex; align-items: center; margin-left: 8px; padding: 1px 8px; border-radius: 9999px; background: var(--amber-dim); color: var(--amber); font-size: var(--text-xs); font-weight: 600; }
  .ts-toggle { display: inline-flex; gap: 2px; padding: 3px; background: var(--surface-sunken); border-radius: 9999px; }
  .ts-btn { min-height: 28px; padding: 0 12px; border: 0; border-radius: 9999px; background: transparent; color: var(--text-muted); font: inherit; font-size: var(--text-sm); font-weight: 500; cursor: pointer; }
  .ts-btn:hover { color: var(--text); }
  .ts-btn.active { background: var(--surface); color: var(--text); box-shadow: var(--shadow-sm); }
  .dash-root.dark .ts-btn.active { background: var(--surface-raised); box-shadow: none; }
  .chart-bars { display: flex; align-items: flex-end; gap: 12px; height: 150px; padding-top: 8px; }
  .chart-bar-col { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; min-width: 0; gap: 8px; }
  .chart-bar-track { width: 100%; flex: 1 1 auto; display: flex; align-items: flex-end; }
  .chart-bar-fill { width: 100%; border-radius: 4px 4px 0 0; background: var(--ink); min-height: 3px; transform-origin: bottom; animation: bar-grow-y 0.4s ease-out both; }
  @keyframes bar-grow-y { from { transform: scaleY(0); } to { transform: scaleY(1); } }
  .chart-bar-fill.is-empty { background: var(--border); }
  .chart-bar-value { font-size: 12px; font-weight: 500; color: var(--text-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }
  .chart-bar-label { font-size: 12px; color: var(--text-muted); white-space: nowrap; }

  /* LIST ROWS */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .breakdown-row { display: flex; align-items: center; gap: 16px; padding: 16px 22px; border-bottom: 1px solid var(--border); }
  .breakdown-row:last-child { border-bottom: none; }
  .breakdown-main { flex: 1 1 auto; min-width: 0; }
  .breakdown-task { font-size: 14px; font-weight: 500; color: var(--text); }
  .breakdown-sub { font-size: 13px; color: var(--text-muted); margin-top: 1px; }
  .breakdown-bar-track { width: 100%; height: 4px; border-radius: 9999px; background: var(--surface-sunken); margin-top: 10px; overflow: hidden; }
  .breakdown-bar-fill { height: 100%; border-radius: 9999px; background: var(--ink); transform-origin: left; animation: bar-grow 0.4s ease-out both; }
  @keyframes bar-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  .breakdown-cost { flex-shrink: 0; font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }

  .notice-card { display: flex; align-items: flex-start; gap: 10px; background: var(--surface-sunken); border: none; border-radius: var(--radius); padding: 12px 16px; font-size: 14px; line-height: 1.5; color: var(--text-body); }
  .notice-card svg { flex-shrink: 0; color: var(--amber); margin-top: 3px; }

  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 20px; gap: 10px; text-align: center; }
  .empty-state-label { font-size: 14px; color: var(--text-muted); }
  .spinner { width: 24px; height: 24px; border: 2px solid var(--border); border-top-color: var(--ink); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Demo-parity layout */
  .page-head { display: flex; flex-direction: column; gap: 5px; }
  .page-eyebrow { display: inline-flex; align-items: center; gap: 6px; align-self: flex-start; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); font-size: 12px; font-weight: 500; color: var(--text-muted); }
  .page-title { font-family: var(--sans); font-size: 18px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.4; color: var(--text); }
  .page-sub { font-size: 13px; line-height: 1.55; color: var(--text-muted); max-width: 70ch; }
  .metric-grid { grid-template-columns: repeat(auto-fit, minmax(168px, 1fr)); gap: 16px; }
  .two-col { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 16px; }
  .card-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
  .card-title { font-size: 14px; font-weight: 600; color: var(--text); }
  .card-note { font-size: 12px; color: var(--text-faint); font-variant-numeric: tabular-nums; }
  .spend-row { margin-bottom: 12px; }
  .spend-row:last-child { margin-bottom: 0; }
  .spend-top { display: flex; justify-content: space-between; gap: 10px; font-size: 12.5px; color: var(--text-muted); margin-bottom: 5px; }
  .spend-amount { font-family: var(--mono); font-size: 11.5px; color: var(--text); }
  .spend-track { height: 4px; border-radius: 9999px; background: var(--surface-sunken); overflow: hidden; }
  .spend-fill { height: 100%; border-radius: 9999px; background: var(--accent-mid); }
  /* AI spend by task, horizontal bars (parity with DemoAiPerformance) */
  .spend-card { display: flex; flex-direction: column; }
  .hbar { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 230px; }
  .hbar-body { position: relative; flex: 1; display: flex; flex-direction: column; min-height: 0; }
  .hbar-grid-layer { position: absolute; top: 0; bottom: 0; left: 116px; right: 12px; pointer-events: none; }
  .hbar-grid { position: absolute; top: 0; bottom: 0; border-left: 1px dashed var(--border); }
  .hbar-row { position: relative; flex: 1; display: grid; grid-template-columns: 112px minmax(0, 1fr); gap: 4px; align-items: center; min-height: 0; }
  .hbar-label { font-size: 10px; color: var(--text-muted); text-align: right; padding-right: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .hbar-track { height: 14px; margin-right: 12px; }
  .hbar-fill { height: 100%; min-width: 2px; border-radius: 0 3px 3px 0; transform-origin: left; animation: bar-grow 0.4s ease-out both; }
  .hbar-axis { position: relative; height: 18px; margin: 4px 12px 0 116px; }
  .hbar-tick { position: absolute; top: 0; transform: translateX(-50%); font-size: 10px; color: var(--text-faint); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .hbar-tick:first-child { transform: none; }
  .data-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .data-table th { text-align: left; padding: 10px 14px; font-family: var(--sans); font-size: 12px; font-weight: 500; color: var(--text-muted); background: var(--surface-raised); border-bottom: 1px solid var(--border); white-space: nowrap; }
  .data-table td { padding: 11px 14px; border-bottom: 1px solid var(--border); color: var(--text-muted); vertical-align: middle; }
  .data-table tbody tr:last-child td { border-bottom: none; }
  .data-table tbody tr:hover td { background: var(--row-hover); }
  .data-table .strong { color: var(--text); font-weight: 600; }
  .data-table .num { font-family: var(--mono); text-align: right; color: var(--text); font-variant-numeric: tabular-nums; }
  @media (max-width: 900px) { .two-col { grid-template-columns: 1fr; } }

  @media (max-width: 640px) {
    .dash-header-inner { padding: 12px 16px; }
    .dash-brand, .dash-header-actions { padding: 0; }
    .dash-main { padding: 24px 16px 40px; }
    .metric-grid { grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); }
    .btn-header span.btn-label { display: none; }
    .chart-bars { height: 110px; }
    .breakdown-row { padding: 14px 16px; }
  }
`;

const CHART_COLORS = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--accent)",
  "var(--text-faint)",
];

const TASK_TYPE_LABELS = {
  daily_briefing: "Daily Briefing",
  opportunity_prioritisation: "Opportunity Prioritisation",
  outreach_email: "Outreach, Email",
  outreach_linkedin: "Outreach, LinkedIn",
  outreach_cold_call_intro: "Outreach, Cold Call Intro",
  outreach_follow_up: "Outreach, Follow-up",
  meeting_prep_notes: "Meeting Prep Notes",
  company_research: "Company Research",
};

export default function AiPerformanceDashboard({
  backTo = "/super-admin",
  embedded = false,
  darkMode: hostDarkMode,
}) {
  const { user, logout } = useAuth();
  const api = useRevenueApi();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownDarkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  // Embedded in CustomerDashboard: the host owns the theme. A separate copy of
  // the state here drifted out of sync (light cards on a dark host).
  const followsHost = embedded && hostDarkMode !== undefined;
  const darkMode = followsHost ? hostDarkMode : ownDarkMode;

  useEffect(() => {
    if (followsHost) return;
    localStorage.setItem("axo_theme", ownDarkMode ? "dark" : "light");
  }, [ownDarkMode, followsHost]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getAiPerformanceDashboard();
      setDashboard(res.dashboard);
    } catch (err) {
      setError("Failed to load AI performance dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live analytics: bucketed time series behind the trend charts.
  const [tsBucket, setTsBucket] = useState("month");
  const [timeseries, setTimeseries] = useState(null);
  const [tsLoading, setTsLoading] = useState(true);

  const loadTimeseries = useCallback(async (bucket) => {
    setTsLoading(true);
    try {
      const res = await api.getAiPerformanceTimeseries({ bucket });
      setTimeseries(res?.timeseries ?? null);
    } catch (err) {
      setTimeseries(null);
    } finally {
      setTsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTimeseries(tsBucket);
  }, [tsBucket, loadTimeseries]);

  // Admin-only: pipeline time-in-stage, for AXO internal bottleneck analysis.
  const canSeeAdmin = !!user?.isSuperAdmin || isInternalUser(user?.email);
  const [stageTimings, setStageTimings] = useState(null);
  const [outreachPerf, setOutreachPerf] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightDim, setInsightDim] = useState("bySic");

  useEffect(() => {
    if (!canSeeAdmin) return;
    let active = true;
    Promise.allSettled([
      api.getStageTimings(),
      api.getOutreachPerformance(),
      api.getInsights(),
    ]).then(([timings, performance, insightRes]) => {
      if (!active) return;
      setStageTimings(
        timings.status === "fulfilled"
          ? (timings.value?.timings ?? null)
          : null,
      );
      setOutreachPerf(
        performance.status === "fulfilled"
          ? (performance.value?.performance ?? null)
          : null,
      );
      setInsights(
        insightRes.status === "fulfilled"
          ? (insightRes.value?.insights ?? null)
          : null,
      );
    });
    return () => {
      active = false;
    };
  }, [canSeeAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const subnavSlot = useSubnavSlot();

  const spendRows = dashboard?.spendByTaskType
    ? [...dashboard.spendByTaskType].sort(
        (a, b) => b.totalCostGbp - a.totalCostGbp,
      )
    : [];
  // Show the top 5 task types and roll the rest into "Other" so the bars and
  // donut add up to the month total instead of silently dropping task types.
  const chartRows =
    spendRows.length > 6
      ? [
          ...spendRows.slice(0, 5),
          {
            taskType: "other",
            requestCount: spendRows
              .slice(5)
              .reduce((sum, r) => sum + r.requestCount, 0),
            totalCostGbp: spendRows
              .slice(5)
              .reduce((sum, r) => sum + r.totalCostGbp, 0),
          },
        ]
      : spendRows;
  const maxSpend = Math.max(...chartRows.map((r) => r.totalCostGbp), 0.01);
  // AI costs are pennies per request, so whole-pound rounding showed "£0".
  const formatSpend = (v) => `£${Number(v).toFixed(Number(v) >= 100 ? 0 : 2)}`;
  // Axis for the horizontal spend bars: 4-ish "nice" steps up to the max.
  const spendAxis = (() => {
    const rawStep = maxSpend / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step =
      [1, 2, 2.5, 5, 10].map((m) => m * mag).find((c) => c >= rawStep) ||
      10 * mag;
    const top = Math.ceil(maxSpend / step - 1e-9) * step;
    const ticks = [];
    for (let v = 0; v <= top + step / 1000; v += step) ticks.push(v);
    return { top, ticks };
  })();
  const formatTick = (v) => `£${Number(Number(v).toFixed(2))}`;
  const totalRequests = spendRows.reduce((sum, r) => sum + r.requestCount, 0);
  const shortTask = (task) =>
    (
      TASK_TYPE_LABELS[task] ||
      String(task || "other")
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
    )
      .replace("Opportunity Prioritisation", "Scoring")
      .replace("Daily Briefing", "Briefing")
      .replace("Company Research", "Research")
      .replace("Meeting Prep Notes", "Meeting prep")
      .replace("Outreach, Cold Call Intro", "Cold call")
      .replace("Outreach, ", "");

  const tsNoun =
    {
      day: "Day",
      week: "Week",
      month: "Month",
      quarter: "Quarter",
      year: "Year",
    }[tsBucket] || "Period";
  const tsDataRevenue = timeseries
    ? timeseries.buckets.map((bucket, i) => ({
        key: bucket.key,
        label: bucket.label,
        axisLabel: bucket.label,
        values: {
          revenue: timeseries.revenueWonGbp[i] ?? 0,
          attributed: timeseries.attributedRevenueGbp[i] ?? 0,
        },
      }))
    : null;
  const tsDataOutreach = timeseries
    ? timeseries.buckets.map((bucket, i) => ({
        key: bucket.key,
        label: bucket.label,
        axisLabel: bucket.label,
        values: {
          drafted: timeseries.outreach.drafted[i] ?? 0,
          sent: timeseries.outreach.sent[i] ?? 0,
          pending: timeseries.outreach.pendingApproval[i] ?? 0,
          rejected: timeseries.outreach.rejected[i] ?? 0,
        },
      }))
    : null;

  const rootInner = (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>
        {embedded
          ? scopeCss(styles, EMBED_SCOPE) +
            `${EMBED_SCOPE} .dash-root { min-height: 0; background: var(--bg); color: var(--text); }
             ${EMBED_SCOPE} .dash-main { max-width: none; padding: 0; }`
          : styles}
      </style>

      {embedded &&
        subnavSlot &&
        createPortal(
          <button
            className={`btn btn-ghost btn-sm${loading ? " is-refreshing" : ""}`}
            onClick={load}
            aria-label="Refresh"
          >
            <span className="btn-label">Refresh</span>
          </button>,
          subnavSlot,
        )}

      {!embedded && (
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link
                to={backTo}
                className="btn-back"
                title="Back to Dashboard"
                aria-label="Back to Dashboard"
              >
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
              <span className="dash-brand-mark" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 2 3 14h8l-1 8 10-12h-8l1-8z"
                  />
                </svg>
              </span>
              <div className="dash-brand-text">
                <div className="dash-brand-name">AI Performance</div>
                <div className="dash-brand-sub">AXO Revenue Intelligence</div>
              </div>
            </div>
            <div className="dash-header-actions">
              {user?.isSuperAdmin && (
                <Link to="/super-admin/ai-governance" className="btn-header">
                  <span className="btn-label">Governance</span>
                </Link>
              )}
              <button
                className={`btn-header${loading ? " is-refreshing" : ""}`}
                onClick={load}
                aria-label="Refresh"
              >
                <svg
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"
                  />
                </svg>
                <span className="btn-label">Refresh</span>
              </button>
              <ThemeSwitch
                iconOnly
                theme={darkMode ? "dark" : "light"}
                onThemeChange={(next, variant, trigger) =>
                  applyThemeWithTransition(next, trigger, setDarkMode, variant)
                }
              />
              <button
                onClick={logout}
                className="btn-header is-accent"
                aria-label="Logout"
              >
                <svg
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                  />
                </svg>
                <span className="btn-label">Logout</span>
              </button>
            </div>
          </div>
        </header>
      )}

      <div className="dash-main">
        {loading ? (
          <div className="empty-state" style={{ width: "100%" }}>
            <Skeleton lines={6} label="Loading AI performance" />
          </div>
        ) : error ? (
          <div className="empty-state">
            <div className="empty-state-label">{error}</div>
          </div>
        ) : (
          <>
            <div className="page-head">
              <span className="page-eyebrow">Revenue intelligence</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div className="page-title">AI Performance</div>
                <div className="ts-toggle" role="group" aria-label="Timeframe">
                  {[
                    ["day", "Daily"],
                    ["week", "Weekly"],
                    ["month", "Monthly"],
                    ["quarter", "Quarterly"],
                    ["year", "Yearly"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`ts-btn${tsBucket === value ? " active" : ""}`}
                      aria-pressed={tsBucket === value}
                      onClick={() => setTsBucket(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="page-sub">
                Every figure below is generated by the AI sales agent. Spend,
                pipeline and revenue are shown together so the return is never a
                number without context.
              </p>
            </div>

            <div className="metric-grid">
              <div className="metric-card">
                <div className="metric-label">
                  AI spend this month
                  <InfoTooltip
                    label="What is AI spend?"
                    content="AI model cost for the workspace so far this month, across every task AXO has run."
                  />
                </div>
                <div className="metric-value">
                  £{Number(dashboard.spend.monthGbp || 0).toFixed(2)}
                </div>
                <div className="metric-note">
                  Across {totalRequests.toLocaleString("en-GB")} tasks
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">
                  Estimated pipeline generated
                  <InfoTooltip
                    label="What is estimated pipeline generated?"
                    content="Combined estimated value of the opportunities surfaced this month, from the Today's priorities scope."
                  />
                </div>
                <div className="metric-value">
                  £
                  {Math.round(dashboard.estimatedPipelineGbp).toLocaleString(
                    "en-GB",
                  )}
                </div>
                <div className="metric-note">From surfaced opportunities</div>
              </div>

              <div className="metric-card">
                <div className="metric-label">
                  Revenue won
                  <InfoTooltip
                    label="What is revenue won?"
                    content="Total value of deals you marked won this month, whether or not AI was involved."
                  />
                </div>
                <div className="metric-value">
                  {dashboard.trackingAvailable?.revenueWonGbp
                    ? `£${Math.round(dashboard.revenueWonGbp).toLocaleString("en-GB")}`
                    : "-"}
                </div>
                <div className="metric-note">All deals won this window</div>
              </div>

              <div className="metric-card">
                <div className="metric-label">
                  Attributed to AI outreach
                  <InfoTooltip
                    label="What is attributed revenue?"
                    content="Revenue from deals linked to an AI-drafted outreach message."
                  />
                </div>
                <div className="metric-value">
                  {dashboard.attributedRevenueGbp != null
                    ? `£${Math.round(dashboard.attributedRevenueGbp).toLocaleString("en-GB")}`
                    : "-"}
                </div>
                <div className="metric-note">
                  {dashboard.revenueWonGbp > 0 &&
                  dashboard.attributedRevenueGbp != null
                    ? `${Math.round((dashboard.attributedRevenueGbp / dashboard.revenueWonGbp) * 100)}% of revenue won`
                    : "Linked to an AI-drafted outreach"}
                </div>
              </div>

              <div className="metric-card is-hero">
                <div className="metric-label">
                  Pipeline ROI
                  <InfoTooltip
                    label="What is pipeline ROI?"
                    content="Estimated pipeline generated for every £1 of AI spend."
                  />
                </div>
                <div className="metric-value">
                  {dashboard.estimatedRoi != null
                    ? `${dashboard.estimatedRoi}×`
                    : "-"}
                </div>
                <div className="metric-note">Pipeline £ per £1 of AI spend</div>
              </div>

              <div className="metric-card">
                <div className="metric-label">
                  Meetings generated
                  <InfoTooltip
                    label="What are meetings generated?"
                    content="Meetings logged against AXO opportunities, from the outcomes your team records."
                  />
                </div>
                <div className="metric-value">
                  {dashboard.trackingAvailable?.meetingsGenerated
                    ? dashboard.meetingsGenerated
                    : "-"}
                </div>
                <div className="metric-note">This month</div>
              </div>
            </div>

            <div className="two-col">
              <div className="chart-card">
                <div className="card-head">
                  <div className="card-title">
                    Revenue won vs attributed revenue
                  </div>
                  <div className="card-note">{tsNoun} · £</div>
                </div>
                {tsLoading || !tsDataRevenue ? (
                  <div className="chart-empty">
                    {tsLoading ? "Loading charts" : "No trend data yet"}
                  </div>
                ) : (
                  <LineChart
                    label="Revenue won vs attributed revenue"
                    categoryLabel={tsNoun}
                    data={tsDataRevenue}
                    series={[
                      {
                        key: "revenue",
                        label: "Revenue won",
                        color: "var(--green)",
                      },
                      {
                        key: "attributed",
                        label: "Attributed revenue",
                        color: "var(--purple)",
                      },
                    ]}
                    height={230}
                    formatValue={(value) =>
                      `£${Math.round(Number(value)).toLocaleString("en-GB")}`
                    }
                    formatTick={(value) => `£${Math.round(Number(value))}`}
                  />
                )}
              </div>

              <div className="chart-card spend-card">
                <div className="card-head">
                  <div className="card-title">AI spend by task</div>
                  <div className="card-note">
                    £{Number(dashboard.spend.monthGbp || 0).toFixed(2)} this
                    month
                  </div>
                </div>
                {spendRows.length === 0 ? (
                  <div className="chart-empty">No AI activity this month</div>
                ) : (
                  <div
                    className="hbar"
                    role="img"
                    aria-label="AI spend by task"
                  >
                    <div className="hbar-body">
                      <div className="hbar-grid-layer" aria-hidden="true">
                        {spendAxis.ticks.map((t) => (
                          <span
                            key={t}
                            className="hbar-grid"
                            style={{ left: `${(t / spendAxis.top) * 100}%` }}
                          />
                        ))}
                      </div>
                      {chartRows.map((row, index) => (
                        <div
                          className="hbar-row"
                          key={row.taskType}
                          title={`${shortTask(row.taskType)}: ${formatSpend(row.totalCostGbp)} · ${row.requestCount} requests`}
                        >
                          <div className="hbar-label">
                            {shortTask(row.taskType)}
                          </div>
                          <div className="hbar-track">
                            <div
                              className="hbar-fill"
                              style={{
                                width: `${(row.totalCostGbp / spendAxis.top) * 100}%`,
                                background:
                                  CHART_COLORS[index % CHART_COLORS.length],
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="hbar-axis" aria-hidden="true">
                      {spendAxis.ticks.map((t) => (
                        <span
                          key={t}
                          className="hbar-tick"
                          style={{ left: `${(t / spendAxis.top) * 100}%` }}
                        >
                          {formatTick(t)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="two-col">
              <div className="chart-card">
                <div className="card-head">
                  <div className="card-title">Outreach drafts by status</div>
                  <div className="card-note">{tsNoun}</div>
                </div>
                {tsLoading || !tsDataOutreach ? (
                  <div className="chart-empty">
                    {tsLoading ? "Loading charts" : "No trend data yet"}
                  </div>
                ) : (
                  <LineChart
                    label="Outreach drafts by status"
                    categoryLabel={tsNoun}
                    data={tsDataOutreach}
                    series={[
                      {
                        key: "drafted",
                        label: "Drafted",
                        color: "var(--purple)",
                      },
                      { key: "sent", label: "Sent", color: "var(--green)" },
                      {
                        key: "pending",
                        label: "Pending approval",
                        color: "var(--amber)",
                      },
                      {
                        key: "rejected",
                        label: "Rejected",
                        color: "var(--red)",
                      },
                    ]}
                    height={210}
                    formatValue={(value) =>
                      Number(value).toLocaleString("en-GB")
                    }
                  />
                )}
              </div>

              <div className="chart-card">
                <div className="card-head">
                  <div className="card-title">Where the AI spend goes</div>
                  <div className="card-note">This month</div>
                </div>
                {spendRows.length === 0 ? (
                  <div className="chart-empty">No AI activity this month</div>
                ) : (
                  <DonutChart
                    data={chartRows.map((row, index) => ({
                      key: row.taskType,
                      label: shortTask(row.taskType),
                      value: row.totalCostGbp,
                      color: CHART_COLORS[index % CHART_COLORS.length],
                    }))}
                    label="AI spend by task"
                    unit=""
                    totalLabel="Spend"
                    size={210}
                    thickness={26}
                    formatValue={(value) => `£${Number(value).toFixed(2)}`}
                  />
                )}
              </div>
            </div>

            <div>
              <div className="section-head">
                <div className="section-title">
                  Outreach performance by method
                  <InfoTooltip
                    label="What is outreach performance by method?"
                    content="How each outreach method performs: drafts sent, the share of contacted companies that replied, rejected drafts and revenue won attributed to that method."
                  />
                </div>
                <div className="section-caption">
                  {canSeeAdmin && outreachPerf?.methods?.length
                    ? "All time"
                    : "This month"}
                </div>
              </div>
              <div className="table-wrap">
                {canSeeAdmin && outreachPerf?.methods?.length ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Method</th>
                        <th style={{ textAlign: "right" }}>Sent</th>
                        <th style={{ textAlign: "right" }}>Replies</th>
                        <th style={{ textAlign: "right" }}>Rejected</th>
                        <th style={{ textAlign: "right" }}>Won</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outreachPerf.methods.map((method) => (
                        <tr key={method.method}>
                          <td className="strong">{method.label}</td>
                          <td className="num">{method.sent}</td>
                          <td className="num">
                            {method.responseRate != null
                              ? `${Math.round(method.responseRate * 100)}%`
                              : "—"}
                          </td>
                          <td className="num">{method.rejected}</td>
                          <td className="num">
                            {method.wonGbp > 0
                              ? `£${Math.round(method.wonGbp).toLocaleString("en-GB")}`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th style={{ textAlign: "right" }}>Drafts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Drafted", dashboard.outreach.drafted],
                        ["Sent", dashboard.outreach.sent],
                        [
                          "Pending approval",
                          dashboard.outreach.pendingApproval,
                        ],
                        ["Rejected", dashboard.outreach.rejected],
                      ].map(([label, value]) => (
                        <tr key={label}>
                          <td className="strong">{label}</td>
                          <td className="num">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {canSeeAdmin && stageTimings?.stages?.length > 0 && (
              <div>
                <div className="section-head">
                  <div className="section-title">
                    Pipeline stage timings
                    <InfoTooltip
                      label="What are stage timings?"
                      content="Internal only. How long leads sit in each pipeline stage, to spot bottlenecks. Days are measured from each lead's last stage change."
                    />
                  </div>
                  <div className="section-caption">
                    {(() => {
                      const stuck = stageTimings.stages.find(
                        (s) => s.stage === stageTimings.bottleneckStage,
                      );
                      return stuck
                        ? `Slowest: ${stuck.label} · ${stuck.avgDays}d avg`
                        : `${stageTimings.totalLeads} leads`;
                    })()}
                  </div>
                </div>
                <div className="table-wrap">
                  {(() => {
                    const max = Math.max(
                      ...stageTimings.stages.map((s) => s.avgDays),
                      1,
                    );
                    return stageTimings.stages.map((s) => (
                      <div className="breakdown-row" key={s.stage}>
                        <div className="breakdown-main">
                          <div className="breakdown-task">
                            {s.label}
                            {s.stage === stageTimings.bottleneckStage && (
                              <span className="stage-bottleneck">
                                bottleneck
                              </span>
                            )}
                          </div>
                          <div className="breakdown-sub">
                            {s.count} {s.count === 1 ? "lead" : "leads"} ·
                            median {s.medianDays}d · p90 {s.p90Days}d · max{" "}
                            {s.maxDays}d
                          </div>
                          <div className="breakdown-bar-track">
                            <div
                              className="breakdown-bar-fill"
                              style={{
                                width: `${Math.max((s.avgDays / max) * 100, s.avgDays > 0 ? 3 : 0)}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="breakdown-cost">{s.avgDays}d</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {canSeeAdmin && outreachPerf?.channels?.length > 0 && (
              <div>
                <div className="section-head">
                  <div className="section-title">
                    Outreach by channel
                    <InfoTooltip
                      label="What is outreach by channel?"
                      content="Internal only. Standalone non-email touches (not already counted as a sent draft) and inbound replies per channel, such as call, LinkedIn or SMS."
                    />
                  </div>
                  <div className="section-caption">All time</div>
                </div>
                <div className="table-wrap">
                  {outreachPerf.channels.map((ch) => (
                    <div className="breakdown-row" key={ch.channel}>
                      <div className="breakdown-main">
                        <div className="breakdown-task">{ch.label}</div>
                        <div className="breakdown-sub">
                          {ch.actions} standalone actions · {ch.replies} replies
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canSeeAdmin && insights && (
              <div>
                <div className="section-head">
                  <div className="section-title">
                    AXO Insights
                    <InfoTooltip
                      label="What is AXO Insights?"
                      content="Internal only. Which company types give disproportionate value: companies, reply rate and revenue won grouped by SIC code, company age or postcode area."
                    />
                  </div>
                  <div
                    className="ts-toggle"
                    role="group"
                    aria-label="Group insights by"
                  >
                    {[
                      ["bySic", "SIC"],
                      ["byAge", "Age"],
                      ["byPostcode", "Postcode"],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`ts-btn${insightDim === value ? " active" : ""}`}
                        aria-pressed={insightDim === value}
                        onClick={() => setInsightDim(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="insights-note">
                  Revenue won per company, across {insights.totalCompanies}{" "}
                  companies. Higher is better; use it to bias toward the company
                  types worth pursuing.
                </p>
                <div className="table-wrap">
                  {(insights[insightDim] || []).length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-label">
                        No data for this grouping yet
                      </div>
                    </div>
                  ) : (
                    (() => {
                      const buckets = insights[insightDim];
                      const max = Math.max(
                        ...buckets.map((b) => b.wonPerCompany),
                        1,
                      );
                      return buckets.map((b) => (
                        <div className="breakdown-row" key={b.key}>
                          <div className="breakdown-main">
                            <div className="breakdown-task">
                              {b.label || b.key}
                            </div>
                            <div className="breakdown-sub">
                              {b.companies} companies · {b.contacted} contacted
                              ·{" "}
                              {b.responseRate != null
                                ? `${Math.round(b.responseRate * 100)}% replied`
                                : "no replies"}{" "}
                              · £{Math.round(b.won).toLocaleString("en-GB")} won
                            </div>
                            <div className="breakdown-bar-track">
                              <div
                                className="breakdown-bar-fill"
                                style={{
                                  width: `${Math.max((b.wonPerCompany / max) * 100, b.wonPerCompany > 0 ? 3 : 0)}%`,
                                }}
                              />
                            </div>
                          </div>
                          <div
                            className="breakdown-cost"
                            title="Revenue won per company"
                          >
                            {b.won > 0
                              ? `£${Math.round(b.wonPerCompany).toLocaleString("en-GB")}`
                              : "-"}
                          </div>
                        </div>
                      ));
                    })()
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return embedded ? (
    <div className="ri-embed-ai-perf">{rootInner}</div>
  ) : (
    rootInner
  );
}
